import React, { useCallback, useRef, useState } from 'react';
import moment from 'moment';
import { useToast } from 'use-toast-mui';
import { useNavigate } from 'react-router-dom';
import { Box, Button, ButtonBase, Checkbox, Dialog, FormControlLabel, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EastIcon from '@mui/icons-material/East';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded';
import { getTicketsByClassTime } from '../../../api/ticket';
import { sendMemberPush } from '../../../api/push';
import { errorMessages } from '../../../utils/methods';
import { scheduleChangePush } from './scheduleMove';

const BRAND = '#4AB3BC';
const WARN = '#C62828';
const PROCESSED = ['ATTENDED', 'ABSENT'];
const ACTIVE = ['RESERVED', ...PROCESSED];

const ATTENDANCE_OPTIONS = [
  { value: 'ATTENDED', label: '출석', color: BRAND, Icon: CheckCircleRoundedIcon },
  { value: 'ABSENT', label: '결석', color: '#E5484D', Icon: CancelRoundedIcon },
];

const hhmm = time => String(time || '').slice(0, 5);
const dotDate = date => String(date).slice(0, 10).replaceAll('-', '.');

/** 옮길 날짜가 예약 수강권(일일권) 이용기간 밖인 첫 회원. 서버도 날짜가 바뀔 때 같은 검사로 406 을 낸다. */
const findOutOfPeriod = (rows, date) =>
  rows.find(
    row =>
      (row.ticketExpireDate && date > row.ticketExpireDate) ||
      (row.ticketStartDate && date < row.ticketStartDate),
  );

/**
 * 수업 날짜·시간 변경 확인(드래그 이동·개인 수정·그룹 수정 공통).
 * - 출결 처리된 회원이 있는데 날짜가 바뀌면: 복구 불가 경고(다른 달이면 급여 정산 안내).
 * - 예약중 회원이 있는데 지난 시간으로 옮기면: 출석/결석 선택(그 상태로 차감).
 * - 회원이 있으면: 앱 푸시 발송 여부.
 * ask() 는 { pastAttendanceStatus, notify, memberIds } 또는 취소 시 null 로 끝나는 Promise 다.
 */
export const useScheduleMoveConfirm = user => {
  const toast = useToast();
  const [state, setState] = useState(null);
  const resolverRef = useRef(null);

  const finish = useCallback(result => {
    setState(null);
    resolverRef.current?.(result);
    resolverRef.current = null;
  }, []);

  // from·to = { date: 'YYYY-MM-DD', startTime: 'HH:mm', endTime: 'HH:mm' }
  const ask = useCallback(
    async ({ courseClassId, from, to }) => {
      const none = { pastAttendanceStatus: undefined, notify: false, memberIds: [] };
      const slotChanged =
        from.date !== to.date || hhmm(from.startTime) !== hhmm(to.startTime) || hhmm(from.endTime) !== hhmm(to.endTime);
      if (!slotChanged || !courseClassId) return none;

      let rows;
      try {
        rows = (await getTicketsByClassTime(courseClassId, user.token)) || [];
      } catch (error) {
        toast.error(errorMessages(error));
        return null;
      }
      const active = rows.filter(row => ACTIVE.includes(row.status));
      if (active.length === 0) return none;

      // 날짜를 바꿀 때 수강권 기간 밖이면 저장 전에 막고 알린다(저장 후 토스트로 알던 것을 앞당김)
      const outOfPeriod = from.date !== to.date ? findOutOfPeriod(active, to.date) : null;
      if (outOfPeriod) {
        return new Promise(resolve => {
          // 앞서 열린 확인이 남아 있으면 취소로 끝낸다(연타·재호출 시 앞 Promise 가 영원히 대기하지 않게)
          resolverRef.current?.(null);
          resolverRef.current = resolve;
          setState({ id: Date.now(), mode: 'period', from, to, row: outOfPeriod });
        });
      }

      const reservedCount = active.filter(row => row.status === 'RESERVED').length;
      const processedCount = active.filter(row => PROCESSED.includes(row.status)).length;
      const dateChanged = from.date !== to.date;
      const past = moment(`${to.date} ${hhmm(to.endTime)}`, 'YYYY-MM-DD HH:mm').isBefore(moment());
      return new Promise(resolve => {
        // 앞서 열린 확인이 남아 있으면 취소로 끝낸다(연타·재호출 시 앞 Promise 가 영원히 대기하지 않게)
          resolverRef.current?.(null);
          resolverRef.current = resolve;
        setState({
          id: Date.now(),
          from,
          to,
          memberIds: [...new Set(active.map(row => row.memberId))],
          reservedCount,
          processedDateWarning: dateChanged && processedCount > 0,
          processedCount,
          monthChanged: from.date.slice(0, 7) !== to.date.slice(0, 7),
          needAttendance: past && reservedCount > 0,
        });
      });
    },
    [user?.token, toast],
  );

  // 저장 성공 후 호출. 푸시 실패는 일정 변경을 되돌리지 않고 알리기만 한다.
  const notify = useCallback(
    async (decision, { branchId, trainerName, className, date, startTime }) => {
      if (!decision?.notify || !decision.memberIds?.length) return;
      try {
        const result = await sendMemberPush(
          branchId,
          { memberIds: decision.memberIds, ...scheduleChangePush({ trainerName, className, date, startTime }) },
          user.token,
        );
        toast.success(`일정 변경 푸시 발송 ${result?.successCount ?? 0}명 (미수신 ${result?.failCount ?? 0}명)`);
      } catch (error) {
        toast.error(`일정은 변경됐지만 푸시 발송에 실패했습니다. ${errorMessages(error)}`);
      }
    },
    [user?.token, toast],
  );

  // 열릴 때마다 선택을 비우려고 요청마다 key 를 바꾼다
  const element = (
    <ScheduleMoveConfirmDialog
      key={state?.id ?? 'closed'}
      state={state}
      onConfirm={finish}
      onCancel={() => finish(null)}
    />
  );
  return { ask, notify, element };
};

const SlotBlock = ({ caption, slot, highlight }) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 0,
      p: '10px 12px',
      borderRadius: '8px',
      border: `1px solid ${highlight ? 'rgba(74, 179, 188, 0.35)' : '#E6E8EB'}`,
      backgroundColor: highlight ? 'rgba(74, 179, 188, 0.06)' : '#F7F8FA',
    }}>
    <Typography sx={{ fontSize: '11px', fontWeight: 600, color: highlight ? BRAND : '#8A929A', mb: '2px' }}>
      {caption}
    </Typography>
    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#1F2A37' }}>
      {moment(slot.date, 'YYYY-MM-DD').format('M월 D일 (dd)')}
    </Typography>
    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: highlight ? BRAND : '#5B6470' }}>
      {hhmm(slot.startTime)} ~ {hhmm(slot.endTime)}
    </Typography>
  </Box>
);

const SectionTitle = ({ children, badge }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mb: '8px' }}>
    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#1F2A37' }}>{children}</Typography>
    {badge && (
      <Box
        sx={{
          px: '8px',
          py: '1px',
          borderRadius: '10px',
          backgroundColor: 'rgba(74, 179, 188, 0.1)',
          color: BRAND,
          fontSize: '12px',
          fontWeight: 600,
        }}>
        {badge}
      </Box>
    )}
  </Box>
);

const ScheduleMoveConfirmDialog = ({ state, onConfirm, onCancel }) => {
  const [attendance, setAttendance] = useState('');
  const [notify, setNotify] = useState(false);

  if (!state) return <Dialog open={false} />;
  if (state.mode === 'period') {
    const { row } = state;
    return (
      <TicketPeriodDialog
        info={{
          memberId: row.memberId,
          memberName: row.memberName,
          ticketName: row.courseTicketName,
          startDate: row.ticketStartDate,
          expireDate: row.ticketExpireDate,
          memberCourseTicketId: row.memberCourseTicketId,
        }}
        targetDate={state.to.date}
        onClose={onCancel}
      />
    );
  }

  const canConfirm = !state.needAttendance || attendance;
  const chosen = ATTENDANCE_OPTIONS.find(option => option.value === attendance);
  const confirm = () =>
    onConfirm({
      pastAttendanceStatus: state.needAttendance ? attendance : undefined,
      notify,
      memberIds: state.memberIds,
    });

  return (
    <Dialog open onClose={onCancel} PaperProps={{ sx: { width: 440, maxWidth: 'calc(100vw - 32px)', borderRadius: '12px' } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: '18px 20px 12px' }}>
        <Typography sx={{ fontSize: '17px', fontWeight: 700 }}>일정 변경</Typography>
        <IconButton size="small" onClick={onCancel}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ p: '0 20px 4px', display: 'flex', flexDirection: 'column', gap: '18px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <SlotBlock caption="기존" slot={state.from} />
          <EastIcon sx={{ color: '#B0B7BF', fontSize: '18px', flexShrink: 0 }} />
          <SlotBlock caption="변경" slot={state.to} highlight />
        </Box>

        {state.needAttendance && (
          <Box>
            <SectionTitle badge={`예약중 ${state.reservedCount}명`}>출결 선택</SectionTitle>
            <Box sx={{ display: 'flex', gap: '8px' }}>
              {ATTENDANCE_OPTIONS.map(({ value, label, color, Icon }) => {
                const selected = attendance === value;
                return (
                  <ButtonBase
                    key={value}
                    onClick={() => setAttendance(value)}
                    sx={{
                      flex: 1,
                      height: '52px',
                      gap: '6px',
                      borderRadius: '10px',
                      border: `1.5px solid ${selected ? color : '#E1E4E8'}`,
                      backgroundColor: selected ? `${color}14` : '#fff',
                      color: selected ? color : '#5B6470',
                      fontSize: '15px',
                      fontWeight: 700,
                      transition: 'all 0.15s',
                      '&:hover': { borderColor: color },
                    }}>
                    <Icon sx={{ fontSize: '20px', color: selected ? color : '#C3C9CF' }} />
                    {label}
                  </ButtonBase>
                );
              })}
            </Box>
            <Typography sx={{ mt: '8px', fontSize: '12px', color: '#8A929A' }}>
              지난 시간이라 바로 출결 처리되며 수강권이 차감됩니다.
            </Typography>
          </Box>
        )}

        {state.processedDateWarning && (
          <Box sx={{ p: '10px 12px', borderRadius: '8px', backgroundColor: '#FFF5F5' }}>
            <Typography sx={{ fontSize: '13px', color: WARN, lineHeight: 1.6 }}>
              출결 처리된 회원 {state.processedCount}명이 있는 수업의 날짜를 변경합니다. 출결 상태와 차감은 그대로
              옮겨지며, 변경 후에는 복구되지 않습니다.
              {state.monthChanged && ' 다른 달로 옮기면 급여 정산 월이 달라질 수 있습니다.'}
            </Typography>
          </Box>
        )}

        <FormControlLabel
          sx={{ m: 0, p: '6px 8px 6px 4px', borderRadius: '8px', border: '1px solid #E6E8EB' }}
          control={<Checkbox size="small" checked={notify} onChange={e => setNotify(e.target.checked)} />}
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <NotificationsNoneRoundedIcon sx={{ fontSize: '18px', color: '#5B6470' }} />
              <Typography sx={{ fontSize: '13px', color: '#1F2A37' }}>
                회원에게 앱 푸시로 알리기 ({state.memberIds.length}명)
              </Typography>
            </Box>
          }
        />
      </Box>

      <Box sx={{ display: 'flex', gap: '8px', p: '16px 20px 20px' }}>
        <Button fullWidth variant="outlined" color="inherit" onClick={onCancel} sx={{ height: '44px', borderColor: '#E1E4E8' }}>
          취소
        </Button>
        <Button
          fullWidth
          variant="contained"
          disabled={!canConfirm}
          onClick={confirm}
          sx={{ height: '44px', fontWeight: 700, backgroundColor: BRAND, '&:hover': { backgroundColor: '#3A9FA8' } }}>
          {state.needAttendance ? (chosen ? `${chosen.label} 처리하고 변경` : '출결을 선택해 주세요') : '변경'}
        </Button>
      </Box>
    </Dialog>
  );
};

/**
 * 날짜가 수강권(일일권) 이용기간 밖일 때 — 닫거나, 회원 상세 상품 탭의 그 수강권 카드로 이동한다.
 * 이동·수정 저장 전, 스케줄 등록에서 수강권을 고를 때, 수정에서 날짜를 바꿀 때 같이 쓴다.
 * info = { memberId, memberName, ticketName, startDate, expireDate, memberCourseTicketId }, targetDate = 'YYYY-MM-DD'
 */
export const TicketPeriodDialog = ({ info, targetDate, onClose, actionLabel = '옮길 수 없습니다' }) => {
  const navigate = useNavigate();
  if (!info) return <Dialog open={false} />;
  const expired = info.expireDate && targetDate > String(info.expireDate).slice(0, 10);
  // 같은 탭에서 회원 상세 상품 탭으로 이동해 그 수강권 카드를 강조한다
  const openTicket = () => {
    onClose();
    navigate(`/member/${info.memberId}?tab=three${info.memberCourseTicketId ? `&ticketId=${info.memberCourseTicketId}` : ''}`);
  };
  return (
    <Dialog open onClose={onClose} PaperProps={{ sx: { width: 440, maxWidth: 'calc(100vw - 32px)', borderRadius: '12px' } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: '18px 20px 12px' }}>
        <Typography sx={{ fontSize: '17px', fontWeight: 700 }}>수강권 이용기간 밖입니다</Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box sx={{ p: '0 20px 4px' }}>
        <Box sx={{ p: '12px', borderRadius: '8px', backgroundColor: '#FFF5F5' }}>
          <Typography sx={{ fontSize: '14px', color: '#1F2A37', lineHeight: 1.7 }}>
            {info.memberName && (
              <>
                <b>{info.memberName}</b> 회원의{' '}
              </>
            )}
            <b>{info.ticketName || '수강권'}</b>
            {expired ? (
              <>
                은 <b style={{ color: WARN }}>{dotDate(info.expireDate)}</b>에 만료됩니다.
              </>
            ) : (
              <>
                은 <b style={{ color: WARN }}>{dotDate(info.startDate)}</b>부터 사용할 수 있습니다.
              </>
            )}
          </Typography>
          <Typography sx={{ fontSize: '13px', color: '#5B6470', mt: '4px' }}>
            {dotDate(targetDate)} 수업에는 {actionLabel}.
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: '8px', p: '16px 20px 20px' }}>
        <Button fullWidth variant="outlined" color="inherit" onClick={onClose} sx={{ height: '44px', borderColor: '#E1E4E8' }}>
          닫기
        </Button>
        <Button
          fullWidth
          variant="contained"
          onClick={openTicket}
          disabled={!info.memberId}
          sx={{ height: '44px', fontWeight: 700, backgroundColor: BRAND, '&:hover': { backgroundColor: '#3A9FA8' } }}>
          회원 상품으로
        </Button>
      </Box>
    </Dialog>
  );
};

/** 날짜(YYYY-MM-DD)가 수강권 기간(시작·만료, 날짜 또는 일시 문자열) 밖인지. 기간 정보가 없으면 밖이 아니다. */
export const isOutOfTicketPeriod = (startDate, expireDate, date) =>
  !!date &&
  ((!!expireDate && date > String(expireDate).slice(0, 10)) || (!!startDate && date < String(startDate).slice(0, 10)));
